
import { useState } from 'react';
import { SetupLoginFormSchema, EMPTY_SETUP_LOGIN_FORM } from './inputs.ts';
import { combineName } from '@shared/user';
import { Button, Field, Input, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import { clearStashedGrant, readStashedGrant } from '@client/shared/auth/index.ts';
import { postSetPassword } from './wizard-api.ts';

export interface SetupLoginStepProps {
  readonly email: string;
  readonly onComplete: (input: { readonly name: string }) => void;
}

export function SetupLoginStep({ email, onComplete }: SetupLoginStepProps) {
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: EMPTY_SETUP_LOGIN_FORM,
    validators: { onSubmit: zodValidator(SetupLoginFormSchema) },
    onSubmit: async ({ value }) => {
      setFormError(null);

      const grant = readStashedGrant();
      if (!grant) {
        setFormError('Your sign-up link has expired. Request a new invitation to continue.');
        return;
      }

      const outcome = await postSetPassword({ grant, password: value.password });
      switch (outcome.kind) {
        case 'ok':
          clearStashedGrant();
          onComplete({ name: combineName(value.firstName, value.lastName) });
          return;
        case 'weak':
          setFormError('Choose a stronger password (at least 8 characters).');
          return;
        case 'invalid-grant':
          clearStashedGrant();
          setFormError('Your sign-up link has expired. Request a new invitation to continue.');
          return;
        case 'error':
          setFormError(outcome.message);
          return;
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <Stack gap={4}>
        <Field label="Email">
          {(wiring) => (
            <Input {...wiring} type="email" value={email} readOnly autoComplete="email" />
          )}
        </Field>

        <form.Field name="firstName">
          {(field) => (
            <TextField
              field={field}
              label="First name"
              type="text"
              autoComplete="given-name"
              placeholder="Dana"
            />
          )}
        </form.Field>

        <form.Field name="lastName">
          {(field) => (
            <TextField
              field={field}
              label="Last name"
              type="text"
              autoComplete="family-name"
              placeholder="Owner"
            />
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <TextField
              field={field}
              label="Create a password"
              type="password"
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          )}
        </form.Field>

        {formError && (
          <Text size="sm" tone="danger" role="alert">
            {formError}
          </Text>
        )}

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" size="lg" block loading={isSubmitting}>
              Continue
            </Button>
          )}
        </form.Subscribe>
      </Stack>
    </form>
  );
}
